/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import Chevron from '../icons/Chevron';
import Date from '../partials/Date';
import Link from '../partials/Link';
import Image from '../partials/Image';
import { useIntersectionObserver } from '../../hooks/useObserver';

import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/items/article.module.scss';

const propTypes = {
    item: AppPropTypes.article.isRequired,
    index: PropTypes.number,
    onClick: PropTypes.func,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    withoutChevron: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    active: false,
    index: null,
    disabled: false,
    onClick: null,
    withoutChevron: false,
    className: null,
};

const Article = ({ item, index, active, disabled, onClick, withoutChevron, className }) => {
    const {
        title = null,
        image = null,
        headerImage = null,
        shortDescription = null,
        type = null,
        url = null,
        publishedAt: articleDate = null,
    } = item || {};

    const realImage = headerImage || image || null;

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-5%' });

    const onClickIndex = useCallback(
        (e) => {
            if (onClick !== null) {
                onClick(e, index);
            }
        },
        [onClick],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.visible]: isIntersecting,
                    [styles.active]: active,
                    [styles.disabled]: disabled,
                    [className]: className !== null,
                },
            ])}
            ref={ref}
        >
            <Link className={styles.link} href={url} onClick={onClickIndex}>
                <div className={styles.inner} title={type !== null ? type : null}>
                    <div className={styles.top}>
                        {articleDate !== null ? (
                            <Date date={articleDate} className={styles.date} />
                        ) : null}
                        {type === 'news' ? (
                            <span className={classNames([styles.date, styles.type])}>
                                <FormattedMessage
                                    defaultMessage="News"
                                    description="Article type"
                                />
                            </span>
                        ) : null}
                        {type === 'embed' ? (
                            <span className={classNames([styles.date, styles.type])}>
                                <FormattedMessage
                                    defaultMessage="Network"
                                    description="Article type"
                                />
                            </span>
                        ) : null}
                        {type === 'newsletter' ? (
                            <span className={classNames([styles.date, styles.type])}>
                                <FormattedMessage
                                    defaultMessage="Newsletter"
                                    description="Article type"
                                />
                            </span>
                        ) : null}
                    </div>
                    <div className={styles.bottom}>
                        {realImage !== null ? (
                            <Image media={realImage} className={styles.image} />
                        ) : (
                            <div className={styles.image} />
                        )}
                        <div className={styles.details}>
                            {title !== null ? <h2 className={styles.title}>{title}</h2> : null}
                            {shortDescription !== null ? (
                                <p className={styles.description}>{shortDescription}</p>
                            ) : null}
                        </div>
                    </div>
                    {!withoutChevron ? (
                        <div className={styles.chevronContainer}>
                            <Chevron className={styles.chevron} />
                        </div>
                    ) : null}
                </div>
            </Link>
        </div>
    );
};

Article.propTypes = propTypes;
Article.defaultProps = defaultProps;

export default Article;
